import { Bell, Search, Settings } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useUnread } from '../../lib/notifications';
import { useSession } from '../../lib/session';
import { MessagesButton, useCanChat } from '../chat/ChatDock';
import { Avatar } from '../community/Avatar';
import { Logo } from '../Logo';
import { NotificationList } from '../notifications/NotificationList';
import { buttonClass } from '../ui';

/** The bell's count: up to 9, then "9+". */
function Count({ unread }: { unread: number | null }) {
  if (!unread) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute top-[3px] right-px box-border grid h-5 min-w-5 place-items-center rounded-full border-2 border-card bg-brand px-[5px] text-[11px] leading-none font-bold text-ink"
    >
      {unread > 9 ? '9+' : unread}
    </span>
  );
}

const bellLabel = (unread: number | null) =>
  unread ? `Notifications, ${unread} new` : 'Notifications';

/** Phone: the bell opens /notifications. */
export function BellLink() {
  const { me } = useSession();
  const unread = useUnread(Boolean(me));
  return (
    <Link
      to="/notifications"
      aria-label={bellLabel(unread)}
      className="relative flex size-11 shrink-0 items-center justify-center rounded-full text-ink"
    >
      <Bell size={22} strokeWidth={1.9} aria-hidden="true" />
      <Count unread={unread} />
    </Link>
  );
}

/** Phone: search opens /search. */
export function SearchLink() {
  return (
    <Link
      to="/search"
      aria-label="Search"
      className="flex size-11 shrink-0 items-center justify-center rounded-full text-ink"
    >
      <Search size={22} strokeWidth={1.9} aria-hidden="true" />
    </Link>
  );
}

/** Computer: the bell, with the newest notifications in a panel under it. */
function BellButton() {
  const unread = useUnread(true);
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const titleId = useId();
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every navigation
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const opener = button.current;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !opener?.contains(target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      if (opener && panel.current?.contains(document.activeElement)) opener.focus();
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        aria-label={bellLabel(unread)}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={`relative flex size-11 items-center justify-center rounded-full text-ink ${open ? 'bg-cream' : 'hover:bg-field'}`}
      >
        <Bell size={22} strokeWidth={1.9} aria-hidden="true" />
        <Count unread={unread} />
      </button>
      {open && (
        <section
          ref={panel}
          role="dialog"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="absolute top-[54px] right-0 z-40 flex max-h-[min(760px,calc(100dvh-96px))] w-[440px] flex-col overflow-hidden rounded-[20px] border border-line bg-card shadow-[0_18px_50px_rgba(27,21,47,0.18)] focus:outline-none"
        >
          <div className="flex items-center justify-between gap-3 px-5 pt-[18px] pb-2.5">
            <h2 id={titleId} className="text-xl font-bold">
              Notifications
            </h2>
            <Link
              to="/account/notifications"
              aria-label="Notification settings"
              className="flex size-9 items-center justify-center rounded-full bg-field text-ink"
            >
              <Settings size={18} strokeWidth={1.9} aria-hidden="true" />
            </Link>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <NotificationList onOpen={() => setOpen(false)} />
          </div>
          <Link
            to="/notifications"
            className="block border-t border-divider p-3.5 text-center text-sm font-semibold underline decoration-brand decoration-2 underline-offset-[3px] hover:decoration-ink"
          >
            See all notifications
          </Link>
        </section>
      )}
    </div>
  );
}

/** The search box in the header: Enter opens the results on /search. */
function SearchBox() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const id = useId();
  return (
    <search className="w-full max-w-[560px]">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const q = text.trim();
          navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
        }}
      >
        <label htmlFor={id} className="sr-only">
          Search games and communities
        </label>
        <div className="relative">
          <Search
            size={20}
            strokeWidth={1.9}
            aria-hidden="true"
            className="pointer-events-none absolute top-3 left-3.5 text-muted"
          />
          <input
            id={id}
            type="search"
            autoComplete="off"
            enterKeyHint="search"
            placeholder="Search games and communities"
            value={text}
            onChange={(event) => setText(event.target.value)}
            className="h-11 w-full rounded-full border border-transparent bg-field pr-4 pl-[42px] text-[15px] placeholder:text-muted focus:border-ink focus:bg-card focus:shadow-[0_0_0_4px_var(--color-cream)] focus:outline-none [&::-webkit-search-cancel-button]:hidden"
          />
        </div>
      </form>
    </search>
  );
}

/**
 * mogzo.com's header on a computer (M33): the logo (to your feed), search in the middle, and
 * messages (M38), the bell and your account at the right. `search` replaces the box (the search page's own).
 */
export function TopBar({ search }: { search?: ReactNode }) {
  const { me } = useSession();
  const canChat = useCanChat();
  return (
    <header className="sticky top-0 z-30 hidden h-16 shrink-0 grid-cols-[280px_minmax(0,1fr)_280px] items-center border-b border-line bg-card pr-6 lg:grid">
      <div className="pl-9">
        <Logo />
      </div>
      <div className="flex justify-center px-6">{search ?? <SearchBox />}</div>
      <div className="flex items-center justify-end gap-2">
        {me && (
          <>
            {canChat && <MessagesButton />}
            <BellButton />
            <Link
              to="/account"
              aria-label="Your account"
              className="flex size-11 items-center justify-center rounded-full hover:bg-field"
            >
              <Avatar name={me.username} />
            </Link>
          </>
        )}
        {me === null && (
          <Link to="/sign-in" className={buttonClass('primary', false)}>
            Sign in
          </Link>
        )}
      </div>
    </header>
  );
}
